import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Download, FileText, Folder, Loader2, Lock, LogIn, ShieldAlert } from "@fg/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { driveApi, type DriveItem } from "@/api/driveApi";
import { returnState } from "@/auth/returnTo";

/**
 * Public landing page for a Drive share link (/shared/:token). Anonymous links
 * open with no sign-in (optional password). Domain / specific-people links are
 * identity-gated: the page sends the signed-in user's token and, if the caller
 * isn't signed in (401) or isn't authorized (403), shows the appropriate
 * prompt. Rendered OUTSIDE ProtectedRoute.
 */
type Status = "loading" | "ok" | "password" | "notfound" | "signin" | "forbidden";

export function SharedPage() {
    const { token } = useParams<{ token: string }>();
    const auth = useAuth();
    const authToken = auth.user?.access_token;
    const [item, setItem] = useState<DriveItem | null>(null);
    const [status, setStatus] = useState<Status>("loading");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [pwError, setPwError] = useState(false);

    const resolve = useCallback(async (pw: string | undefined) => {
        if (!token) { setStatus("notfound"); return; }
        const { status: httpStatus, item: resolved } = await driveApi.resolveShare(token, pw, authToken);
        if (httpStatus === 200 && resolved) { setItem(resolved); setStatus("ok"); return; }
        if (httpStatus === 401) { setStatus("signin"); return; }   // restricted — must sign in
        if (httpStatus === 403) { setStatus("forbidden"); return; } // signed in, not on the list
        // 404 — invalid/expired OR wrong/missing password (indistinguishable).
        if (pw === undefined) setStatus("password");
        else { setPwError(true); setStatus("password"); }
    }, [token, authToken]);

    // Re-resolve once auth finishes loading (so a returning signed-in user is
    // recognised for a restricted link).
    useEffect(() => { if (!auth.isLoading) void resolve(undefined); }, [resolve, auth.isLoading]);

    const download = async () => {
        if (!token) return;
        try {
            await driveApi.downloadShared(token, item?.name ?? "download", password || undefined, authToken);
        } catch { /* surfaced via the button state; user can retry */ }
    };

    if (status === "loading") {
        return <Centered><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /><p className="text-sm text-muted-foreground">Opening shared file…</p></Centered>;
    }

    if (status === "notfound") {
        return (
            <Centered>
                <ShieldAlert className="h-10 w-10 text-amber-500" />
                <h1 className="text-lg font-semibold">Link unavailable</h1>
                <p className="max-w-sm text-center text-sm text-muted-foreground">This share link is invalid, has expired, or the file was removed.</p>
            </Centered>
        );
    }

    if (status === "signin") {
        return (
            <Centered>
                <Lock className="h-8 w-8 text-muted-foreground" />
                <h1 className="text-lg font-semibold">Sign in to open</h1>
                <p className="max-w-sm text-center text-sm text-muted-foreground">This item is shared with specific people or your organization. Sign in with an authorized account to continue.</p>
                <Button className="mt-1 gap-2" onClick={() => void auth.signinRedirect({ state: returnState() })}><LogIn className="h-4 w-4" /> Sign in</Button>
            </Centered>
        );
    }

    if (status === "forbidden") {
        return (
            <Centered>
                <ShieldAlert className="h-10 w-10 text-destructive" />
                <h1 className="text-lg font-semibold">No access</h1>
                <p className="max-w-sm text-center text-sm text-muted-foreground">
                    You're signed in as {auth.user?.profile.email ?? "this account"}, but this item isn't shared with you. Ask the owner for access.
                </p>
            </Centered>
        );
    }

    if (status === "password") {
        return (
            <Centered>
                <Lock className="h-8 w-8 text-muted-foreground" />
                <h1 className="text-lg font-semibold">Password required</h1>
                <p className="text-sm text-muted-foreground">This shared file is protected.</p>
                <form className="mt-2 flex w-full max-w-xs flex-col gap-2" onSubmit={(e) => {
                    e.preventDefault();
                    if (!password.trim() || submitting) return;
                    setSubmitting(true); setPwError(false);
                    void resolve(password).finally(() => setSubmitting(false));
                }}>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" autoFocus aria-label="Share password" />
                    {pwError ? <p className="text-xs text-destructive">Incorrect password. Try again.</p> : null}
                    <Button type="submit" disabled={!password.trim() || submitting}>{submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Unlock</Button>
                </form>
            </Centered>
        );
    }

    const isFolder = item?.kind === "Folder";
    return (
        <Centered>
            <div className="flex flex-col items-center gap-4 rounded-xl border bg-card p-8 shadow-sm">
                {isFolder ? <Folder className="h-12 w-12 text-primary" /> : <FileText className="h-12 w-12 text-primary" />}
                <div className="text-center">
                    <h1 className="max-w-xs truncate text-lg font-semibold" title={item?.name}>{item?.name}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{isFolder ? "Shared folder" : `${formatBytes(item?.sizeBytes ?? 0)} · shared file`}</p>
                </div>
                {isFolder ? (
                    <p className="max-w-xs text-center text-xs text-muted-foreground">Folder sharing preview isn't available yet — ask the owner to share individual files.</p>
                ) : (
                    <Button onClick={() => void download()} className="gap-2"><Download className="h-4 w-4" /> Download</Button>
                )}
            </div>
            <p className="text-[11px] text-muted-foreground">Shared via Drive</p>
        </Centered>
    );
}

function Centered({ children }: { children: React.ReactNode }) {
    return <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-muted/20 p-6">{children}</div>;
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
}
